import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import Container from '@mui/material/Container'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { useQuery } from '@tanstack/react-query'
import KNLoader from 'components/KNLoader/KNLoader'
import { Role } from 'context/authentication/Role.types'
import { useVehiclesView } from 'context/VehiclesViewContext'
import { analyticsEvent, analyticsPageView } from 'global/helpers/analytics'
import { hasAccess } from 'global/helpers/authorization'
import { getSearchCondition, getValueCondition } from 'global/helpers/filtering'
import { getRequestErrorMessage } from 'global/helpers/requestError'
import useDialog from 'global/hooks/useDialog'
import { type ReactElement, useCallback, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import CreateOrEditVehicleDialog from './CreateOrEditVehicleDialog'
import VehicleFilters from './VehicleFilters'
import { vehiclesQueryOptions } from './VehicleManager.service'
import { VehicleType } from './VehicleManager.types'
import VehicleTable from './VehicleTable'

const VehicleManager = (): ReactElement => {
  const { t } = useTranslation()
  const { filters } = useVehiclesView()
  const vehiclesQuery = useQuery(vehiclesQueryOptions)
  const hasTelematics = vehiclesQuery.data?.some((vehicle) => vehicle.telematicsReady) ?? true
  const filteredVehiclesData = useMemo(
    () =>
      (vehiclesQuery.data ?? []).filter((vehicle) => {
        const keywordsCondition = getSearchCondition([
          vehicle.licensePlate,
        ], filters.keywords)

        const vehicleTypesCondition = getValueCondition<VehicleType>(
          vehicle.vehicleType,
          filters.vehicleTypes,
        )

        return [keywordsCondition, vehicleTypesCondition].every((condition: boolean) => condition)
      }),
    [vehiclesQuery.data, filters],
  )
  const createOrEditVehicleDialog = useDialog<typeof CreateOrEditVehicleDialog>()

  useEffect(() => {
    analyticsPageView('polestar/cs/vehicles_list')
  }, [])

  const handleAddClick = useCallback((): void => {
    createOrEditVehicleDialog.handleOpen()
    analyticsEvent('polestar_cs_create_vehicle_button_clicked')
  }, [])

  return (
    <Container maxWidth="xxl">
      {vehiclesQuery.isPending && (
        <KNLoader>
          <Typography>{t('screens.cs.vehicle_manager.loading')}</Typography>
        </KNLoader>
      )}
      {vehiclesQuery.isError && (
        <Alert severity="error">{getRequestErrorMessage(vehiclesQuery.error)}</Alert>
      )}
      {vehiclesQuery.isSuccess && (
        <>
          <Paper sx={{ padding: 2 }}>
            <Stack direction="column" spacing={1} mb={1}>
              <VehicleFilters />
              <Stack
                spacing={1}
                direction={{ xs: 'column', md: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'start', md: 'center' }}
                mb={2}
              >
                <Typography variant="h6">
                  {t('screens.cs.vehicle_manager.vehicles_count', { count: filteredVehiclesData.length })}
                </Typography>
                {hasAccess(Role.Editor) && !hasTelematics && (
                  <Button
                    onClick={handleAddClick}
                    variant="contained"
                    color="secondary"
                    size="small"
                  >
                    {t('screens.cs.vehicle_manager.card.actions.new')}
                  </Button>
                )}
              </Stack>
            </Stack>
            <VehicleTable vehicles={filteredVehiclesData} />
          </Paper>
          {hasAccess(Role.Editor) && !hasTelematics && (
            <CreateOrEditVehicleDialog
              open={createOrEditVehicleDialog.isOpen}
              onAction={createOrEditVehicleDialog.handleAction}
              onClose={createOrEditVehicleDialog.handleClose}
            />
          )}
        </>
      )}
    </Container>
  )
}

export default VehicleManager
